import type { ReactChild, ReactPortal } from "react";
import { StructMetric, StructPeriod } from "./structures";
import moment from "moment";

export const compareStrings = (a: string, b: string): number =>
  a < b ? -1 : a > b ? 1 : 0;

export const formatInt = (num: number | string | null | undefined): string => {
  if (num == null) {
    return "0";
  } else if (typeof num === "number") {
    return num.toLocaleString("en-GB", { maximumFractionDigits: 0 });
  } else {
    return parseInt(num).toLocaleString("en-GB", { maximumFractionDigits: 0 });
  }
};

export const formatPerc = (num: number | null | undefined): string => {
  if (num == null || !Number.isFinite(num)) {
    return "0%";
  } else {
    return (
      (num * 100).toLocaleString("en-GB", {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
      }) + "%"
    );
  }
};

export const formatNumAndPerc = (
  part: number,
  whole: number,
): { perc: string; num: number } => {
  return {
    perc: formatPerc(part / whole),
    num: part,
  };
};

export const percColour = (perc: number): string => {
  if (!Number.isFinite(perc)) return "#777777";

  let backgroundColor = "#c00000"; // Dark Red
  if (perc >= 100) {
    backgroundColor = "#0070c0"; // Blue
  } else if (perc >= 75) {
    backgroundColor = "#70ad47"; // Green
  } else if (perc >= 50) {
    backgroundColor = "#ff7f00"; // Orange
  } else if (perc >= 25) {
    backgroundColor = "#ff0000"; // Red
  }

  return backgroundColor;
};

export const scales = {
  blue: ["#eff3ff", "#c6dbef", "#9ecae1", "#6baed6", "#3182bd", "#08519c"],
  orange: ["#feedde", "#fdd0a2", "#fdae6b", "#fd8d3c", "#e6550d", "#a63603"],
  red: ["#fee5d9", "#fcbba1", "#fc9272", "#fb6a4a", "#de2d26", "#a50f15"],
  green: ["#edf8e9", "#c7e9c0", "#a1d99b", "#74c476", "#31a354", "#006d2c"],
};

const percActionColour = (
  perc: number,
  scale: keyof typeof scales = "blue",
): string => {
  if (!Number.isFinite(perc)) return "#777777";

  const selectedScale = scales[scale];

  let backgroundColor = selectedScale[0]; // Dark Red
  if (perc >= 1) {
    backgroundColor = selectedScale[5]; // Blue
  } else if (perc >= 0.8) {
    backgroundColor = selectedScale[4]; // Blue
  } else if (perc >= 0.6) {
    backgroundColor = selectedScale[3]; // Green
  } else if (perc >= 0.4) {
    backgroundColor = selectedScale[2]; // Orange
  } else if (perc >= 0.2) {
    backgroundColor = selectedScale[1]; // Red
  }
  return backgroundColor;
};
const DAYS_SINCE_START = moment().diff(
  moment("01 Jun 2026", "DD MMM YYYY"),
  "days",
);

enum PeriodDaysCount {
  "current" = DAYS_SINCE_START,
  "attendance" = DAYS_SINCE_START,
  "last30days" = 30,
  "last7days" = 7,
}

export const mapStyle = (
  actions: number,
  period: StructPeriod,
  population: number,
  scale: keyof typeof scales = "blue",
): { perc: number; backgroundColor: string } => {
  const SCALING_FACTOR = 0.0025;
  const EXPECTED_ACTIONS =
    SCALING_FACTOR * population * PeriodDaysCount[period];
  const perc = actions / EXPECTED_ACTIONS;

  return { perc, backgroundColor: percActionColour(perc, scale) };
};

export const statsStyle = (
  achieved: number,
  total: number,
): { fontWeight: string; color: string; backgroundColor: string } => {
  const perc = Math.round((achieved / total) * 1000) / 10.0;
  return {
    fontWeight: "bold",
    color: "white",
    backgroundColor: percColour(perc),
  };
};

export const statsStyleBorder = (achieved: number, total: number): any => {
  const perc = Math.round((achieved / total) * 1000) / 10.0;
  return {
    fontWeight: "bold",
    outlineStyle: "solid",
    outlineWidth: "5px",
    outlineOffset: "-10px",
    verticalAlign: "middle",
    outlineColor: percColour(perc),
  };
};

export const statsClass = (fraction: number): string => {
  const perc = Math.round(fraction * 1000) / 10.0;

  let backgroundColor = "perc-darkred"; // Dark Red
  if (perc >= 100) {
    backgroundColor = "perc-blue"; // Blue
  } else if (perc >= 75) {
    backgroundColor = "perc-green"; // Green
  } else if (perc >= 50) {
    backgroundColor = "perc-orange"; // Orange
  } else if (perc >= 25) {
    backgroundColor = "perc-red"; // Red
  }

  return backgroundColor;
};

export const firstCap = (str: string): string =>
  str.charAt(0).toUpperCase() + str.substring(1);

// see https://fettblog.eu/react-types-for-children-are-broken/
type ReactNode =
  | ReactChild
  | ReactNode[]
  | ReactPortal
  | boolean
  | null
  | undefined;

export type WithChildren<T = {}> = T & { children?: ReactNode };

export const getMetricNum = (
  node: any,
  metric: StructMetric,
  period: StructPeriod,
): number => {
  let val;
  if (metric === "both") {
    val = node?.canvassing[period] + node?.activity.attendance[period];
  } else if (metric === "activity") {
    val = node?.[metric]?.attendance[period];
  } else {
    val = node?.[metric][period];
  }
  return typeof val === "number" ? val : 0;
};
